/**
 * FOUNDING-1 / FOUNDING-3 — the Founding Member program (Sep 28).
 *
 * Everyone who asked for early access is a Founding Member (Rennan,
 * Sep 28: "everyone who is an early access member gets founding access").
 * Each gets a private link — orbitstudiocrm.com/founding/<token>, no
 * sign-in needed to open it — to the Founding Pass: three years paid
 * once, of Orbit ($999.99) or Orbit Business ($1,499.99). Either Pass
 * comes with a Launch Edition kit: ONE hoodie (design + size), ONE pin
 * and ONE patch. The kit is not a gift on its own —
 * "They only get it if they buy the founding pass." A member can pick
 * their kit before paying; the picks are held and only become an order
 * once the Pass is paid.
 *
 * This file is the kit catalog (drawn from src/config/merch.ts) and the
 * Pass's numbers. The page is src/pages/FoundingKit.tsx; the reads and
 * writes are src/services/founding.ts.
 *
 * `id` is what gets stored with a member's picks. Never rename or reuse
 * an id once links are out. To stop offering a design, set
 * `retired: true` rather than deleting it: it leaves the picker, and
 * anyone who already chose it still sees what they chose. Names and
 * descriptions live in merch.ts and can change freely.
 */
import type { SubscriptionPlan } from "@/services/billing";
import {
  HOODIE_SIZES,
  MERCH_HOODIES,
  MERCH_PATCHES,
  MERCH_PINS,
  MERCH_SPECS,
  type HoodieSize,
  type MerchItem,
} from "@/config/merch";

/** The year — on the pin card, the shipping label and the footer. */
export const FOUNDING_YEAR = "MMXXVI";

/** The direct line every Founding Member gets. */
export const FOUNDING_LINE = "founding@orbitstudiocrm.com";

/** `/founding/demo` — a sample member, nothing is saved. */
export const FOUNDING_DEMO_TOKEN = "demo";

/**
 * Deadlines are written and shown in Pacific time — the same clock the
 * launch ran on (PUBLIC_LAUNCH_DATE, config/launch.ts) — so "Oct 31"
 * reads as Oct 31 wherever the member is.
 */
export const FOUNDING_TIME_ZONE = "America/Los_Angeles";

/**
 * When picks close, if the server hasn't said. The server's
 * `picks_close_at` (founding_kit_get) is the real one — this is only the
 * demo's date and a fallback. 11:59pm Pacific (still PDT on Oct 31).
 */
export const KIT_PICKS_CLOSE_FALLBACK = "2026-10-31T23:59:00-07:00";

// ── The catalog ─────────────────────────────────────────────────────────

/**
 * A design as the kit page and Mission Control show it. Every field comes
 * from the Launch Edition catalog (src/config/merch.ts) — the shop and the
 * kit sell and offer the same products under the same names, so a
 * member's H21 and a shop order's H21 are one thing.
 */
export interface KitDesign {
  /** Stored with the member's picks. Stable forever — see merch.ts. */
  id: string;
  /** The product's own name ("Manifest"). */
  name: string;
  /** Hoodies: the design's code on the Launch Edition sheet (H1…H24). */
  code?: string;
  /** Hoodies: the fleece colour. */
  colorway?: string;
  /** The product's description. */
  blurb: string;
  /** Construction, from the design sheet. */
  detail: string;
  /** Large product shot, transparent. */
  image: string;
  /** Small version for the picker tiles. */
  thumb: string;
  /** Hoodies only: the back print. */
  back?: string;
  /** No longer offered; still shown to members who picked it. */
  retired?: boolean;
}

const toKit = (m: MerchItem): KitDesign => ({
  id: m.id,
  name: m.name,
  code: m.code,
  colorway: m.colorway,
  blurb: m.description,
  detail: m.detail,
  image: m.image,
  thumb: m.thumb,
  back: m.kind === "hoodie" ? m.back : undefined,
});

export const KIT_HOODIES: KitDesign[] = MERCH_HOODIES.map(toKit);
export const KIT_PINS: KitDesign[] = MERCH_PINS.map(toKit);
export const KIT_PATCHES: KitDesign[] = MERCH_PATCHES.map(toKit);

/**
 * What the page shows before anything is picked — the hero's kit and the
 * stage's first pictures. Manifest (H21) is the hoodie the Launch Edition
 * leads with; Creator Corps says "Shoot · Edit · Deliver".
 */
export const KIT_DEFAULTS = {
  hoodie: "hoodie-h21",
  pin: "pin-happy-nova",
  patch: "patch-creator-corps",
};

/** The shop's spec lines, shown under each picker. */
export const HOODIE_SPECS = MERCH_SPECS.hoodie;
export const PATCH_SPECS = MERCH_SPECS.patch;
export const PIN_SPECS = MERCH_SPECS.pin;

export { HOODIE_SIZES };
export type { HoodieSize };

/**
 * One line under the size chips (fit, blank, a size-chart link in
 * words). Empty = nothing shown. Say only what's true of the blank
 * Rennan orders.
 */
export const HOODIE_FIT_NOTE = "Unisex sizes, XS–3XL · oversized drop-shoulder fit.";

// ── Shipping ────────────────────────────────────────────────────────────

/**
 * Where the kit ships. Every inhabited country except those under
 * comprehensive US embargo. To ship to the US only, make this ["US"] —
 * the country menu disappears and the form says so.
 */
export const SHIP_COUNTRIES: readonly string[] = (
  "AD AE AF AG AI AL AM AO AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ " +
  "BL BM BN BO BQ BR BS BT BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR " +
  "CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR " +
  "GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GT GU GW GY HK HN HR HT HU ID " +
  "IE IL IM IN IQ IS IT JE JM JO JP KE KG KH KI KM KN KR KW KY KZ LA LB LC " +
  "LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS " +
  "MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG " +
  "PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI " +
  "SJ SK SL SM SN SO SR SS ST SV SX SZ TC TD TG TH TJ TK TL TM TN TO TR TT " +
  "TV TW TZ UA UG US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW"
).split(" ");

export const DEFAULT_SHIP_COUNTRY = "US";

// ── The Founding Pass ───────────────────────────────────────────────────

/** Which Pass: Orbit, or Orbit Business. Stored on the purchase (`tier`). */
export type PassTier = "orbit" | "business";

export interface PassTierInfo {
  id: PassTier;
  /** The plan it pays for — its name and regular prices come from billing.ts. */
  plan: SubscriptionPlan;
  /** Paid once, up front, for `years`. */
  price: number;
}

/**
 * The Passes as the page describes them. Each plan's name and regular
 * prices come from src/services/billing.ts (PLAN_PRICING / PLAN_LABELS),
 * so the comparison can't drift from what Orbit charges.
 *
 * Sep 28 (Rennan): "two founding prices and products. 1st is 999.99 …
 * for the orbit pro plan. And the 1499.99 is for orbit business plan but
 * they both get hoodie pin and patch." ("Orbit Pro" is the plan the app
 * calls Orbit.) Each is three years, charged once with no trial, then
 * the Founding Member rate — 20% off the plan's yearly price — for as
 * long as the account keeps it. In Stripe that's a one-time price plus a
 * yearly Founding-rate price whose first three years a coupon makes free
 * (Sep 29: not a price that recurs "every 3 years"). The Oct 31 close
 * is shared. There is no cap (Oct 2, Rennan: "any early access user
 * should have the opportunity to do it"): every early-access member can
 * buy one until then, and nothing says how many there are.
 * The server is the authority on every number the member pays or is
 * limited by:
 *   supabase/functions/_shared/foundingTiers.ts   prices, renewals
 *   founding_pass_*() in the FOUNDING-3 migration  closing time
 * Change one, change both.
 *
 * `onSale` is the switch for both Passes: off, a member's page shows the
 * Pass without a working button (and says so), and the sign-up plan step
 * never offers it. The demo always shows it.
 */
export const FOUNDING_PASS = {
  onSale: true as boolean,
  tiers: {
    orbit: { id: "orbit", plan: "orbit", price: 999.99 },
    business: { id: "business", plan: "orbit_business", price: 1499.99 },
  } as Record<PassTier, PassTierInfo>,
  /** The order they're offered in. The first is the one picked to start with. */
  order: ["orbit", "business"] as PassTier[],
  years: 3,
  /** Off the plan's regular yearly price after the term, for as long as they keep it. */
  afterPct: 20,
  /** Last moment to buy — and to pick or change a kit. */
  closesAt: "2026-10-31T23:59:00-07:00",
};

export const DEFAULT_PASS_TIER: PassTier = "orbit";

export const isPassTier = (v: unknown): v is PassTier => v === "orbit" || v === "business";

/** The lowest Pass price — "from $999.99". */
export const PASS_FROM_PRICE = Math.min(...FOUNDING_PASS.order.map((t) => FOUNDING_PASS.tiers[t].price));
